/**
 * RunsheetSelector — picker used on the project / workbench page.
 *
 * Loads the runsheets attached to the stream's streamKey (via the
 * `available-runsheets` endpoint) and lets the user swap to a different one.
 * Swapping clears the assignment's `processedAt` server-side, which forces
 * streamcontroller to re-match on the next cut cycle.
 */

import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  useAvailableRunsheetsForStream,
  useSetStreamRunsheetAssignment,
} from '@/adapters/runsheet-v2';

interface RunsheetSelectorProps {
  streamId?: string;
  onRunsheetChanged?(): void;
}

const RunsheetSelector: FC<RunsheetSelectorProps> = ({ streamId, onRunsheetChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading } = useAvailableRunsheetsForStream(streamId);
  const setAssignment = useSetStreamRunsheetAssignment();
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    if (data?.currentRunsheetId) {
      setSelected(data.currentRunsheetId);
    }
  }, [data?.currentRunsheetId]);

  const handleChange = async (nextId: string): Promise<void> => {
    if (!streamId || !nextId || nextId === selected) return;
    const previous = selected;
    setSelected(nextId);
    try {
      await setAssignment.mutateAsync({ streamId, runsheetId: nextId });
      enqueueSnackbar('Your runsheet has been successfully reassigned', { variant: 'success' });
      onRunsheetChanged?.();
    } catch (err) {
      console.error(err);
      setSelected(previous);
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to reassign runsheet', {
        variant: 'error',
      });
    }
  };

  if (!streamId) {
    return (
      <Typography variant="body2" color="textSecondary">
        No stream associated with this project.
      </Typography>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="textSecondary">
          Loading runsheets…
        </Typography>
      </Box>
    );
  }

  const runsheets = data?.runsheets ?? [];

  if (runsheets.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No runsheets available for this stream key.
      </Typography>
    );
  }

  return (
    <FormControl fullWidth size="small" sx={{ minWidth: 240 }}>
      <InputLabel id="runsheet-select-label">Runsheet</InputLabel>
      <Select
        labelId="runsheet-select-label"
        value={selected}
        label="Runsheet"
        onChange={(e) => handleChange(e.target.value)}
        disabled={setAssignment.isLoading}
      >
        {runsheets.map((r) => (
          <MenuItem key={r.runsheetId} value={r.runsheetId}>
            {r.title}
            {r.runsheetId === data?.currentRunsheetId && (
              <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                (current)
              </Typography>
            )}
          </MenuItem>
        ))}
      </Select>
      {setAssignment.isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="textSecondary">
            Swapping…
          </Typography>
        </Box>
      )}
    </FormControl>
  );
};

export default RunsheetSelector;
